/**
 * WateringHoldBanner — why this site is not watering, and how it got there.
 *
 * Four states share one banner on purpose, because they are one story told in
 * order and splitting them would let a page show the ending without the
 * middle:
 *
 *   STOPPING   the pumps are off and their confirmation is being waited on, or
 *              the valves are closing. Each pump is listed with its own tick,
 *              because "waiting" with nothing named reads as "stuck".
 *
 *   STALLED    a pump did NOT confirm. Red, loud, and explicit that the valves
 *              have been left OPEN on purpose — the one state here that needs
 *              somebody to walk to the pump. It is never phrased as a failure
 *              of the stop: the site did the right thing.
 *
 *   FAILED     the stop did not reach the site at all. The commonest cause is
 *              an XBee bridge too old to know the command, which acks
 *              "unsupported command type 'STOP_ALL'" and carries on running its
 *              own timers. The server-side hold still applies, so nothing NEW
 *              will be scheduled — but that site may still be watering right
 *              now, and this must never be drawn as "stopped".
 *
 *   HELD       everything is off and nothing will start. Amber, not green: a
 *              farm that will not water tonight is not a success message, and
 *              this is the banner that has to still be legible at 6am to
 *              somebody who did not press the button.
 *
 * Resume needs an irrigation admin seat while Stop needs none. When the reader
 * has not got one the button is not simply hidden — the banner says who can
 * lift it, because a page that silently offers no way out of a hold is how a
 * farm stays dry for a weekend.
 */

import { OctagonX, AlertTriangle, PauseCircle, CheckCircle2, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import type { WateringStopStatus } from '../../services/espnowIrrigation';
import { formatDateTimeInTz } from '../../utils/timezone';

interface WateringHoldBannerProps {
  status: WateringStopStatus | null;
  /** IANA zone the times are shown in — never the browser's. */
  timezone: string;
  /** Irrigation admin seat: the gate the Resume endpoint itself applies. */
  canResume: boolean;
  busy?: boolean;
  onResume: () => Promise<void> | void;
  className?: string;
}

export default function WateringHoldBanner({
  status,
  timezone,
  canResume,
  busy = false,
  onResume,
  className,
}: WateringHoldBannerProps) {
  if (!status) return null;

  const request = status.request;
  const phase = request && !request.finished_at ? request.phase : null;
  const stalled = request?.phase === 'stalled';
  const failed = request?.phase === 'failed';
  const stopping = phase === 'pumps' || phase === 'valves' || phase === 'delegated';
  // Whether the water actually stopped is a different question from whether the
  // schedule is held, and the two answers diverge exactly here. Keeping them
  // separate is what stops a failed stop being painted as a quiet farm.
  const alarming = stalled || failed;

  // Nothing to say: never stopped, or stopped and since resumed.
  if (!status.paused && !stopping && !alarming) return null;

  const when = status.paused_at ? formatDateTimeInTz(status.paused_at, timezone) : null;
  const who = status.paused_by || request?.requested_by;

  const tone = alarming
    ? { box: 'border-red-300 bg-red-50', text: 'text-red-900', sub: 'text-red-800' }
    : stopping
      ? { box: 'border-blue-300 bg-blue-50', text: 'text-blue-900', sub: 'text-blue-800' }
      : { box: 'border-amber-300 bg-amber-50', text: 'text-amber-900', sub: 'text-amber-800' };

  const Icon = alarming ? AlertTriangle : stopping ? Loader2 : PauseCircle;

  return (
    <div className={`rounded-xl border p-4 ${tone.box} ${className ?? ''}`}>
      <div className="flex flex-wrap items-start gap-3">
        <Icon
          className={`mt-0.5 h-5 w-5 shrink-0 ${tone.text} ${stopping ? 'animate-spin' : ''}`}
        />
        <div className="min-w-0 flex-1">
          <h3 className={`text-sm font-semibold ${tone.text}`}>
            {stalled
              ? 'A pump did not confirm it stopped'
              : failed
                ? 'The stop did not reach this site'
                : stopping
                  ? 'Stopping watering…'
                  : 'Watering is stopped at this site'}
          </h3>

          <p className={`mt-1 text-sm ${tone.sub}`}>
            {alarming
              ? request?.detail
              : stopping
                ? request?.detail || 'Switching the pumps off first. Valves follow once they confirm.'
                : [
                    who ? `Stopped by ${who}` : 'Stopped',
                    when ? ` at ${when}` : '',
                    '. No program will run — including tonight’s — until it is resumed.',
                  ].join('')}
          </p>

          {status.pause_reason && !stopping && !alarming && (
            <p className={`mt-1 text-sm italic ${tone.sub}`}>“{status.pause_reason}”</p>
          )}

          {/* Each pump, named, with whether the site has actually reported it
              clear. A spinner with no detail behind it is indistinguishable
              from a page that has stopped updating. */}
          {stopping && (request?.pumps.length ?? 0) > 0 && (
            <ul className="mt-2 space-y-1">
              {request!.pumps.map((p) => (
                <li key={`${p.router_mac}-${p.relay_index}`} className="flex items-center gap-2 text-sm text-blue-900">
                  {p.confirmed ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  )}
                  <span className="truncate">
                    {p.label} — {p.confirmed ? 'confirmed off' : 'waiting for confirmation'}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {stalled && (
            <p className="mt-2 text-sm font-semibold text-red-900">
              The valves it feeds are still open on purpose, so the pump is not shut in.
              Go and check the pump.
            </p>
          )}

          {failed && (
            <p className="mt-2 text-sm font-semibold text-red-900">
              Nothing new will be scheduled, but this site may still be watering right now.
              Check it, and switch the valves off from its own page if it is.
            </p>
          )}

          {status.paused && !stopping && !canResume && (
            <p className={`mt-2 text-xs ${tone.sub}`}>
              Resuming needs an Irrigation Admin licence seat.
            </p>
          )}
        </div>

        {status.paused && !stopping && canResume && (
          <Button
            variant="secondary"
            size="sm"
            isLoading={busy}
            disabled={busy}
            onClick={() => void onResume()}
          >
            Resume watering
          </Button>
        )}
      </div>

      {/* Only on the stalled path: everything else is either self-explanatory
          or already said above. */}
      {stalled && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-red-800">
          <OctagonX className="h-3.5 w-3.5 shrink-0" />
          Watering stays held either way — nothing new will start.
        </p>
      )}
    </div>
  );
}
